import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';

const DISPLAY_CHANNEL = 'display-control';

// ─── PIN Lock ─────────────────────────────────────────────────────────────────
const CONTROL_PIN = '1234'; // Change this to your preferred PIN

const PinLock = ({ onUnlock }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleDigit = (d) => {
    if (pin?.length >= 4) return;
    const next = pin + d;
    setPin(next);
    setError(false);
    if (next?.length === 4) {
      if (next === CONTROL_PIN) {
        onUnlock();
      } else {
        setShake(true);
        setError(true);
        setTimeout(() => { setPin(''); setShake(false); }, 700);
      }
    }
  };

  const handleDelete = () => setPin(p => p?.slice(0, -1));

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-xs">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center">
            <Icon name="Lock" size={32} color="#f59e0b" />
          </div>
        </div>

        <h1 className="text-2xl font-bold text-white text-center mb-1">Display Control</h1>
        <p className="text-gray-500 text-sm text-center mb-8">Enter PIN to access the control panel</p>

        {/* PIN dots */}
        <div className={`flex justify-center gap-4 mb-8 ${shake ? 'animate-bounce' : ''}`}>
          {[0, 1, 2, 3]?.map(i => (
            <div
              key={i}
              className={`w-4 h-4 rounded-full border-2 transition-all ${
                i < pin?.length
                  ? error ? 'bg-red-500 border-red-500' : 'bg-amber-500 border-amber-500' :'border-gray-600'
              }`}
            />
          ))}
        </div>

        {error && (
          <p className="text-red-400 text-sm text-center mb-4">Incorrect PIN. Try again.</p>
        )}

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3">
          {['1','2','3','4','5','6','7','8','9','','0','⌫']?.map((key, i) => (
            <button
              key={i}
              onClick={() => key === '⌫' ? handleDelete() : key ? handleDigit(key) : null}
              disabled={!key}
              className={`h-16 rounded-2xl text-xl font-semibold transition-all active:scale-95 ${
                key === '⌫' ?'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  : key
                  ? 'bg-gray-800 text-white hover:bg-gray-700 active:bg-gray-600' :'opacity-0 pointer-events-none'
              }`}
            >
              {key}
            </button>
          ))}
        </div>

        <p className="text-gray-700 text-xs text-center mt-8">
          Default PIN: 1234 · Change in display/control/index.jsx
        </p>
      </div>
    </div>
  );
};

// ─── Control Panel ────────────────────────────────────────────────────────────
const ANIMATION_OPTIONS = [
  { value: 'fade', label: 'Fade', icon: 'Sunset' },
  { value: 'slide-left', label: 'Slide Left', icon: 'ArrowLeft' },
  { value: 'slide-right', label: 'Slide Right', icon: 'ArrowRight' },
  { value: 'slide-up', label: 'Slide Up', icon: 'ArrowUp' },
  { value: 'zoom', label: 'Zoom In', icon: 'ZoomIn' },
  { value: 'zoom-out', label: 'Zoom Out', icon: 'ZoomOut' },
  { value: 'none', label: 'None', icon: 'Minus' },
];

const EASING_OPTIONS = [
  { value: 'ease-in-out', label: 'Ease In-Out' },
  { value: 'ease', label: 'Ease' },
  { value: 'ease-in', label: 'Ease In' },
  { value: 'ease-out', label: 'Ease Out' },
  { value: 'linear', label: 'Linear' },
  { value: 'spring', label: 'Spring' },
];

const ControlPanel = ({ onLock }) => {
  const [displayState, setDisplayState] = useState({
    currentIndex: 0,
    isPaused: false,
    timingOverride: null,
    totalSlides: 0,
    currentSlideTitle: '',
    animSettings: { animation: 'fade', duration: 600, easing: 'ease-in-out' },
  });
  const [connected, setConnected] = useState(false);
  const [localTiming, setLocalTiming] = useState(10);
  const [localDuration, setLocalDuration] = useState(600);
  const [localEasing, setLocalEasing] = useState('ease-in-out');
  const [localAnimation, setLocalAnimation] = useState('fade');
  const [activeTab, setActiveTab] = useState('controls'); // controls | animation
  const [lastAction, setLastAction] = useState(null);
  const channelRef = useRef(null);

  const flash = (msg) => {
    setLastAction(msg);
    setTimeout(() => setLastAction(null), 1500);
  };

  const send = useCallback((action, value) => {
    channelRef?.current?.send({
      type: 'broadcast',
      event: 'control',
      payload: { action, value },
    });
  }, []);

  // Request state from display
  const requestState = useCallback(() => {
    channelRef?.current?.send({
      type: 'broadcast',
      event: 'control',
      payload: { action: 'request_state' },
    });
  }, []);

  useEffect(() => {
    const channel = supabase?.channel(DISPLAY_CHANNEL, {
      config: { broadcast: { self: true } },
    });

    channel?.on('broadcast', { event: 'state' }, ({ payload }) => {
        if (payload) {
          setDisplayState(payload);
          if (payload?.timingOverride !== null && payload?.timingOverride !== undefined) {
            setLocalTiming(payload?.timingOverride);
          }
          if (payload?.animSettings) {
            setLocalAnimation(payload?.animSettings?.animation || 'fade');
            setLocalDuration(payload?.animSettings?.duration || 600);
            setLocalEasing(payload?.animSettings?.easing || 'ease-in-out');
          }
        }
      })?.subscribe((status) => {
        setConnected(status === 'SUBSCRIBED');
        if (status === 'SUBSCRIBED') {
          setTimeout(requestState, 500);
        }
      });

    channelRef.current = channel;

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [requestState]);

  const handlePauseToggle = () => {
    send(displayState?.isPaused ? 'play' : 'pause');
    flash(displayState?.isPaused ? 'Resumed' : 'Paused');
  };

  const handleNext = () => {
    send('next');
    flash('Next slide →');
  };

  const handlePrev = () => {
    send('prev');
    flash('← Prev slide');
  };

  const handleSetTiming = () => {
    send('set_timing', localTiming);
    flash(`Timing set to ${localTiming}s`);
  };

  const handleClearTiming = () => {
    send('clear_timing');
    flash('Timing reset to defaults');
  };

  const handleRefresh = () => {
    send('refresh');
    flash('Refreshing live data…');
  };

  const handleApplyAnimation = () => {
    send('set_animation', {
      animation: localAnimation,
      duration: localDuration,
      easing: localEasing,
    });
    flash('Animation updated');
  };

  const slideProgress = displayState?.totalSlides > 0
    ? Math.round(((displayState?.currentIndex + 1) / displayState?.totalSlides) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col max-w-sm mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between px-5 pt-safe pt-6 pb-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-500/20 rounded-xl flex items-center justify-center">
            <Icon name="Monitor" size={16} color="#f59e0b" />
          </div>
          <div>
            <h1 className="text-base font-bold text-white leading-tight">Display Control</h1>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-green-400' : 'bg-gray-600'}`} />
              <span className="text-xs text-gray-500">{connected ? 'Connected' : 'Connecting…'}</span>
            </div>
          </div>
        </div>
        <button
          onClick={onLock}
          className="w-9 h-9 bg-gray-800 rounded-xl flex items-center justify-center text-gray-400 hover:text-white transition-colors"
        >
          <Icon name="Lock" size={16} />
        </button>
      </div>

      {/* Current slide info */}
      <div className="mx-5 mt-4 bg-gray-900 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Now Showing</span>
          <span className="text-xs text-gray-400">
            {displayState?.currentIndex + 1} / {displayState?.totalSlides || '—'}
          </span>
        </div>
        <p className="text-white font-semibold text-sm truncate">
          {displayState?.currentSlideTitle || 'No slide info'}
        </p>
        <div className="mt-3 w-full bg-gray-800 rounded-full h-1.5">
          <div
            className="bg-amber-500 h-1.5 rounded-full transition-all duration-300"
            style={{ width: `${slideProgress}%` }}
          />
        </div>
        <div className="flex items-center gap-2 mt-2">
          {displayState?.isPaused && (
            <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Icon name="Pause" size={10} color="#f59e0b" /> Paused
            </span>
          )}
          {displayState?.timingOverride !== null && displayState?.timingOverride !== undefined && (
            <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded-full flex items-center gap-1">
              <Icon name="Clock" size={10} color="#60a5fa" /> {displayState?.timingOverride}s override
            </span>
          )}
        </div>
      </div>

      {/* Action flash */}
      {lastAction && (
        <div className="mx-5 mt-3 bg-green-500/20 border border-green-500/30 rounded-xl px-4 py-2.5 text-green-400 text-sm font-medium text-center">
          {lastAction}
        </div>
      )}

      {/* Tabs */}
      <div className="flex mx-5 mt-4 bg-gray-900 rounded-xl p-1 gap-1">
        {[
          { id: 'controls', label: 'Controls', icon: 'Sliders' },
          { id: 'animation', label: 'Animation', icon: 'Sparkles' },
        ]?.map(tab => (
          <button
            key={tab?.id}
            onClick={() => setActiveTab(tab?.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab?.id
                ? 'bg-amber-500 text-white' :'text-gray-400 hover:text-white'
            }`}
          >
            <Icon name={tab?.icon} size={14} color={activeTab === tab?.id ? '#fff' : undefined} />
            {tab?.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto px-5 pb-8">
        {activeTab === 'controls' && (
          <div className="space-y-4 mt-4">
            {/* Playback controls */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Playback</p>
              <div className="flex gap-3">
                <button
                  onClick={handlePrev}
                  className="flex-1 flex flex-col items-center gap-2 py-4 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all active:scale-95"
                >
                  <Icon name="SkipBack" size={22} color="#e5e7eb" />
                  <span className="text-xs text-gray-400">Previous</span>
                </button>
                <button
                  onClick={handlePauseToggle}
                  className={`flex-1 flex flex-col items-center gap-2 py-4 rounded-xl transition-all active:scale-95 ${
                    displayState?.isPaused
                      ? 'bg-amber-500 hover:bg-amber-400' :'bg-gray-800 hover:bg-gray-700'
                  }`}
                >
                  <Icon name={displayState?.isPaused ? 'Play' : 'Pause'} size={22} color="#fff" />
                  <span className="text-xs text-white/80">{displayState?.isPaused ? 'Resume' : 'Pause'}</span>
                </button>
                <button
                  onClick={handleNext}
                  className="flex-1 flex flex-col items-center gap-2 py-4 bg-gray-800 hover:bg-gray-700 rounded-xl transition-all active:scale-95"
                >
                  <Icon name="SkipForward" size={22} color="#e5e7eb" />
                  <span className="text-xs text-gray-400">Next</span>
                </button>
              </div>
            </div>

            {/* Slide jump */}
            {displayState?.totalSlides > 0 && (
              <div className="bg-gray-900 rounded-2xl p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Jump to Slide</p>
                <div className="grid grid-cols-5 gap-2">
                  {Array.from({ length: displayState?.totalSlides }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => { send('goto', i); flash(`Jumped to slide ${i + 1}`); }}
                      className={`h-10 rounded-xl text-sm font-semibold transition-all active:scale-95 ${
                        i === displayState?.currentIndex
                          ? 'bg-amber-500 text-white' :'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Timing override */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Slide Timing Override</p>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-300">Duration</span>
                <span className="text-amber-400 font-bold text-sm">{localTiming}s</span>
              </div>
              <input
                type="range"
                min={3}
                max={60}
                value={localTiming}
                onChange={e => setLocalTiming(parseInt(e?.target?.value))}
                className="w-full accent-amber-500 mb-3"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSetTiming}
                  className="flex-1 py-2.5 bg-amber-500 hover:bg-amber-400 text-white rounded-xl text-sm font-semibold transition-all active:scale-95"
                >
                  Apply {localTiming}s
                </button>
                {(displayState?.timingOverride !== null && displayState?.timingOverride !== undefined) && (
                  <button
                    onClick={handleClearTiming}
                    className="px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm font-medium transition-all active:scale-95"
                  >
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Refresh live data */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Live Data</p>
              <button
                onClick={handleRefresh}
                className="w-full flex items-center justify-center gap-3 py-3.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm font-semibold transition-all active:scale-95"
              >
                <Icon name="RefreshCw" size={18} color="#fff" />
                Refresh All Live Data
              </button>
              <p className="text-xs text-gray-600 mt-2 text-center">
                Reloads projects, resources, and Gantt data on the display
              </p>
            </div>
          </div>
        )}

        {activeTab === 'animation' && (
          <div className="space-y-4 mt-4">
            {/* Animation type */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Transition Style</p>
              <div className="grid grid-cols-2 gap-2">
                {ANIMATION_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setLocalAnimation(opt?.value)}
                    className={`flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-medium transition-all active:scale-95 ${
                      localAnimation === opt?.value
                        ? 'bg-amber-500 text-white' :'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    <Icon name={opt?.icon} size={16} color={localAnimation === opt?.value ? '#fff' : '#9ca3af'} />
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Duration */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Transition Duration</p>
                <span className="text-amber-400 font-bold text-sm">{localDuration}ms</span>
              </div>
              <input
                type="range"
                min={100}
                max={2000}
                step={50}
                value={localDuration}
                onChange={e => setLocalDuration(parseInt(e?.target?.value))}
                className="w-full accent-amber-500"
              />
              <div className="flex justify-between text-xs text-gray-600 mt-1">
                <span>100ms</span><span>2000ms</span>
              </div>
            </div>

            {/* Easing */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Easing Curve</p>
              <div className="grid grid-cols-2 gap-2">
                {EASING_OPTIONS?.map(opt => (
                  <button
                    key={opt?.value}
                    onClick={() => setLocalEasing(opt?.value)}
                    className={`py-2.5 px-3 rounded-xl text-sm font-medium transition-all active:scale-95 ${
                      localEasing === opt?.value
                        ? 'bg-amber-500 text-white' :'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    }`}
                  >
                    {opt?.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Apply animation */}
            <button
              onClick={handleApplyAnimation}
              className="w-full flex items-center justify-center gap-3 py-4 bg-amber-500 hover:bg-amber-400 text-white rounded-2xl text-base font-bold transition-all active:scale-95"
            >
              <Icon name="Sparkles" size={20} color="#fff" />
              Apply Animation Settings
            </button>

            {/* Current display state */}
            <div className="bg-gray-900 rounded-2xl p-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Current Display Settings</p>
              <div className="space-y-1.5">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Animation</span>
                  <span className="text-gray-300 capitalize">{displayState?.animSettings?.animation || '—'}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Duration</span>
                  <span className="text-gray-300">{displayState?.animSettings?.duration || '—'}ms</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Easing</span>
                  <span className="text-gray-300">{displayState?.animSettings?.easing || '—'}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Export ──────────────────────────────────────────────────────────────
const DisplayControlPage = () => {
  const [unlocked, setUnlocked] = useState(false);

  return unlocked
    ? <ControlPanel onLock={() => setUnlocked(false)} />
    : <PinLock onUnlock={() => setUnlocked(true)} />;
};

export default DisplayControlPage;
